import {
  FINTRAC_STR_CONDUCTOR_TYPE_CODE_OPTIONS,
  FINTRAC_STR_DEVICE_CODE_OPTIONS,
  FINTRAC_STR_YES_NO_OPTIONS,
  FINTRAC_STR_RELATIONSHIP_OF_CONDUCTOR_CODE_OPTIONS,
  FINTRAC_STR_DEVICE_CODE,
  FINTRAC_STR_TRANSACTION_METHOD_CODES,
  FINTRAC_STR_RELATIONSHIP_OF_CONDUCTOR_CODE,
  FINTRAC_STR_DATETIME_FORMAT,
  FINTRAC_STR_DEFINITION_TYPE_CODE_MAP,
} from 'app/modules/fintracStr/formConstants';
import {
  getDependentPaths,
  getUniqueDefinitionOptions,
} from 'app/modules/fintracStr/formUtils';
import {
  FintracFormSingleField,
  FintracFormValue,
} from 'app/modules/fintracStr/models';
import { isFalse, isFieldBlank } from 'app/modules/fintracStr/renderConditions';
import {
  U21FormFieldTypes,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import { format } from 'date-fns';
import { isUndefined } from 'lodash';

const isNotOnlineAndOtherMethod = (value: FintracFormValue) =>
  value !== FINTRAC_STR_TRANSACTION_METHOD_CODES.OTHER.value &&
  value !== FINTRAC_STR_TRANSACTION_METHOD_CODES.ONLINE.value;

export const FINTRAC_STR_TRANSACTION_STARTING_ACTIONS_CONDUCTORS_FIELDS: FintracFormSingleField =
  {
    type: 'section',
    repeatable: true,
    label:
      'Information About the Person Conducting or Attempting to Conduct the Transaction',
    repeatableButtonLabel: 'Conductor Information',
    name: 'conductors',
    mustExistInPayload: true,
    mustHaveValueIfExist: true,
    dependentPaths: (sectionIndex, fieldSectionIndex) =>
      getDependentPaths(
        undefined,
        fieldSectionIndex && fieldSectionIndex.length === 1
          ? [
              `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.conductorIndicator`,
            ]
          : undefined,
        sectionIndex,
        fieldSectionIndex,
      ),
    hideIfTrue: {
      and: [
        {
          path: (sectionIndex, fieldSectionIndex) =>
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.conductorIndicator`
              : '',
          conditions: (value) => isFalse(value),
        },
      ],
    },
    fields: [
      {
        type: U21FormFieldTypes.SELECT,
        name: 'typeCode',
        label: 'Conductor type',
        fieldProps: (_, mutators, sectionIndex, fieldSectionIndex) =>
          fieldSectionIndex && fieldSectionIndex.length === 2
            ? {
                options: FINTRAC_STR_CONDUCTOR_TYPE_CODE_OPTIONS,
                onChange: () => {
                  mutators.setFieldValueUntyped(
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].refId`,
                    '',
                  );
                },
              }
            : { options: FINTRAC_STR_CONDUCTOR_TYPE_CODE_OPTIONS },
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.SELECT,
        name: 'refId',
        label: 'Subject name',
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 2
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}]`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].typeCode`,
                  'definitions', // Need to listen to definitions since getUniqueDefinitionOptions relies on definitions
                ]
              : ['definitions'],
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 2
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].typeCode`
                  : '',
              conditions: (value) => isFieldBlank(value),
            },
          ],
        },
        fieldProps: (flattenedValues, _, sectionIndex, fieldSectionIndex) => ({
          noOptionsText: ((): string => {
            if (
              !isUndefined(sectionIndex) &&
              fieldSectionIndex &&
              fieldSectionIndex.length === 2
            ) {
              const label =
                FINTRAC_STR_DEFINITION_TYPE_CODE_MAP[
                  flattenedValues[
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].typeCode`
                  ]
                ];

              if (label) {
                return `No options available. Please create a new "${label}" definition in the definitions tab.`;
              }
            }

            return `No options available. Please create a new definition in the definitions tab.`;
          })(),
          options: ((): U21SelectOptionProps<string>[] =>
            !isUndefined(sectionIndex) &&
            fieldSectionIndex &&
            fieldSectionIndex.length === 2
              ? getUniqueDefinitionOptions(
                  flattenedValues,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}]`,
                )
              : [])(),
        }),
      },
      {
        type: 'section',
        repeatable: false,
        label: 'Details',
        name: 'details',
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        isSectionWrapperHidden: true,
        fields: [
          [
            {
              type: U21FormFieldTypes.TEXT,
              name: 'clientNumber',
              label: 'Client number',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
            },
            {
              type: U21FormFieldTypes.TEXT,
              name: 'emailAddress',
              label: 'Email address',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
            },
          ],
          [
            {
              type: U21FormFieldTypes.TEXT,
              name: 'username',
              label: 'Username',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
              dependentPaths: (sectionIndex, fieldSectionIndex) =>
                getDependentPaths(
                  undefined,
                  [
                    `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                  ],
                  sectionIndex,
                  fieldSectionIndex,
                ),
              hideIfTrue: {
                and: [
                  {
                    path: (sectionIndex) =>
                      `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                    conditions: (value) => isNotOnlineAndOtherMethod(value),
                  },
                ],
              },
            },
            {
              type: U21FormFieldTypes.TEXT,
              name: 'url',
              label: 'URL',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
            },
          ],
          [
            {
              type: U21FormFieldTypes.SELECT,
              name: 'typeOfDeviceCode',
              label: 'Type of device',
              fieldProps: {
                options: FINTRAC_STR_DEVICE_CODE_OPTIONS,
              },
              mustExistInPayload: false,
              mustHaveValueIfExist: true,
              dependentPaths: (sectionIndex, fieldSectionIndex) =>
                getDependentPaths(
                  undefined,
                  [
                    `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                  ],
                  sectionIndex,
                  fieldSectionIndex,
                ),
              hideIfTrue: {
                and: [
                  {
                    path: (sectionIndex) =>
                      `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                    conditions: (value) => isNotOnlineAndOtherMethod(value),
                  },
                ],
              },
            },
            {
              type: U21FormFieldTypes.TEXT,
              name: 'typeOfDeviceOther',
              label: 'Other device type description',
              dependentPaths: (sectionIndex, fieldSectionIndex) =>
                getDependentPaths(
                  undefined,
                  fieldSectionIndex && fieldSectionIndex.length === 2
                    ? [
                        `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].details.typeOfDeviceCode`,
                      ]
                    : undefined,
                  sectionIndex,
                  fieldSectionIndex,
                ),
              hideIfTrue: {
                and: [
                  {
                    path: (sectionIndex, fieldSectionIndex) =>
                      fieldSectionIndex && fieldSectionIndex.length === 2
                        ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].details.typeOfDeviceCode`
                        : '',
                    conditions: (value) =>
                      value !== FINTRAC_STR_DEVICE_CODE.OTHER.value, // Other (4)
                  },
                ],
              },
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
            },
          ],
          [
            {
              type: U21FormFieldTypes.TEXT,
              name: 'deviceIdentifierNumber',
              label: 'Device identifier number',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
              dependentPaths: (sectionIndex, fieldSectionIndex) =>
                getDependentPaths(
                  undefined,
                  [
                    `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                  ],
                  sectionIndex,
                  fieldSectionIndex,
                ),
              hideIfTrue: {
                and: [
                  {
                    path: (sectionIndex) =>
                      `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                    conditions: (value) => isNotOnlineAndOtherMethod(value),
                  },
                ],
              },
            },
            {
              type: U21FormFieldTypes.TEXT,
              name: 'internetProtocolAddress',
              label: 'IP address',
              required: false,
              mustExistInPayload: false,
              mustHaveValueIfExist: false,
              dependentPaths: (sectionIndex, fieldSectionIndex) =>
                getDependentPaths(
                  undefined,
                  [
                    `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                  ],
                  sectionIndex,
                  fieldSectionIndex,
                ),
              hideIfTrue: {
                and: [
                  {
                    path: (sectionIndex) =>
                      `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                    conditions: (value) =>
                      value !==
                        FINTRAC_STR_TRANSACTION_METHOD_CODES.OTHER.value &&
                      value !==
                        FINTRAC_STR_TRANSACTION_METHOD_CODES.ONLINE.value,
                  },
                ],
              },
            },
          ],
          {
            type: U21FormFieldTypes.DATE_TIME_PICKER,
            name: 'dateTimeOfOnlineSession',
            label: 'Date and time of online session',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: true,
            fieldProps: {
              maxDate: new Date(),
            },
            dependentPaths: (sectionIndex, fieldSectionIndex) =>
              getDependentPaths(
                undefined,
                [
                  `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                ],
                sectionIndex,
                fieldSectionIndex,
              ),
            hideIfTrue: {
              and: [
                {
                  path: (sectionIndex) =>
                    `transactions[${sectionIndex}].suspiciousTransactionDetails.methodCode`,
                  conditions: (value) => isNotOnlineAndOtherMethod(value),
                },
              ],
            },
            formToPayloadValueConversion: (values) => {
              if (values instanceof Date) {
                return {
                  dateTimeOfOnlineSession: format(
                    values,
                    FINTRAC_STR_DATETIME_FORMAT,
                  ),
                };
              }

              return {};
            },
          },
          {
            type: U21FormFieldTypes.RADIO_GROUP,
            name: 'onBehalfOfIndicator',
            label:
              'Was this transaction conducted or attempted on behalf of another person or entity?',
            required: true,
            mustExistInPayload: false,
            mustHaveValueIfExist: true,
            defaultValue: true,
            fieldProps: {
              options: FINTRAC_STR_YES_NO_OPTIONS,
              horizontal: true,
            },
          },
        ],
      },
      {
        type: 'section',
        repeatable: true,
        label:
          'Information About the Person on Whose Behalf the Transaction Was Conducted or Attempted',
        repeatableButtonLabel: 'Third Party Information',
        name: 'onBehalfOfs',
        mustExistInPayload: true,
        mustHaveValueIfExist: false,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 2
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].details.onBehalfOfIndicator`,
                ]
              : undefined,
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 2
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].details.onBehalfOfIndicator`
                  : '',
              conditions: (value) => isFalse(value),
            },
          ],
        },
        fields: [
          {
            type: U21FormFieldTypes.SELECT,
            name: 'typeCode',
            label: 'Conductor type',
            fieldProps: (_, mutators, sectionIndex, fieldSectionIndex) =>
              fieldSectionIndex && fieldSectionIndex.length === 3
                ? {
                    options: FINTRAC_STR_CONDUCTOR_TYPE_CODE_OPTIONS,
                    onChange: () => {
                      mutators.setFieldValueUntyped(
                        `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}].refId`,
                        '',
                      );
                    },
                  }
                : { options: FINTRAC_STR_CONDUCTOR_TYPE_CODE_OPTIONS },
            mustExistInPayload: true,
            mustHaveValueIfExist: true,
          },
          {
            type: U21FormFieldTypes.SELECT,
            name: 'refId',
            label: 'Subject name',
            mustExistInPayload: true,
            mustHaveValueIfExist: true,
            dependentPaths: (sectionIndex, fieldSectionIndex) =>
              getDependentPaths(
                undefined,
                fieldSectionIndex && fieldSectionIndex.length === 3
                  ? [
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs`,
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}]`,
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}].typeCode`,
                      'definitions', // Need to listen to definitions since getUniqueDefinitionOptions relies on definitions
                    ]
                  : ['definitions'],
                sectionIndex,
                fieldSectionIndex,
              ),
            hideIfTrue: {
              and: [
                {
                  path: (sectionIndex, fieldSectionIndex) =>
                    fieldSectionIndex && fieldSectionIndex.length === 3
                      ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}].typeCode`
                      : '',
                  conditions: (value) => isFieldBlank(value),
                },
              ],
            },
            fieldProps: (
              flattenedValues,
              _,
              sectionIndex,
              fieldSectionIndex,
            ) => ({
              noOptionsText: ((): string => {
                if (
                  !isUndefined(sectionIndex) &&
                  fieldSectionIndex &&
                  fieldSectionIndex.length === 3
                ) {
                  const label =
                    FINTRAC_STR_DEFINITION_TYPE_CODE_MAP[
                      flattenedValues[
                        `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}].typeCode`
                      ]
                    ];

                  if (label) {
                    return `No options available. Please create a new "${label}" definition in the definitions tab.`;
                  }
                }

                return `No options available. Please create a new definition in the definitions tab.`;
              })(),
              options: ((): U21SelectOptionProps<string>[] =>
                !isUndefined(sectionIndex) &&
                fieldSectionIndex &&
                fieldSectionIndex.length === 3
                  ? getUniqueDefinitionOptions(
                      flattenedValues,
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs`,
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}]`,
                    )
                  : [])(),
            }),
          },
          {
            type: 'section',
            repeatable: false,
            label: 'Details',
            name: 'details',
            mustExistInPayload: true,
            mustHaveValueIfExist: false,
            isSectionWrapperHidden: true,
            fields: [
              [
                {
                  type: U21FormFieldTypes.TEXT,
                  name: 'clientNumber',
                  label: 'Client number',
                  required: false,
                  mustExistInPayload: false,
                  mustHaveValueIfExist: false,
                },
                {
                  type: U21FormFieldTypes.TEXT,
                  name: 'emailAddress',
                  label: 'Email address',
                  required: false,
                  mustExistInPayload: false,
                  mustHaveValueIfExist: false,
                },
              ],
              {
                type: U21FormFieldTypes.TEXT,
                name: 'url',
                label: 'URL',
                required: false,
                mustExistInPayload: false,
                mustHaveValueIfExist: false,
              },
              [
                {
                  type: U21FormFieldTypes.SELECT,
                  name: 'relationshipOfConductorCode',
                  label: 'Relationship of conductor',
                  fieldProps: {
                    options: FINTRAC_STR_RELATIONSHIP_OF_CONDUCTOR_CODE_OPTIONS,
                  },
                  required: false,
                  mustExistInPayload: false,
                  mustHaveValueIfExist: true,
                },
                {
                  type: U21FormFieldTypes.TEXT,
                  name: 'relationshipOfConductorOther',
                  label: 'Other relationship of conductor description',
                  dependentPaths: (sectionIndex, fieldSectionIndex) =>
                    getDependentPaths(
                      undefined,
                      fieldSectionIndex && fieldSectionIndex.length === 3
                        ? [
                            `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}].details.relationshipOfConductorCode`,
                          ]
                        : undefined,
                      sectionIndex,
                      fieldSectionIndex,
                    ),
                  hideIfTrue: {
                    and: [
                      {
                        path: (sectionIndex, fieldSectionIndex) =>
                          fieldSectionIndex && fieldSectionIndex.length === 3
                            ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].conductors[${fieldSectionIndex[1]}].onBehalfOfs[${fieldSectionIndex[2]}].details.relationshipOfConductorCode`
                            : '',
                        conditions: (value) =>
                          value !==
                          FINTRAC_STR_RELATIONSHIP_OF_CONDUCTOR_CODE.OTHER
                            .value,
                      },
                    ],
                  },
                  mustExistInPayload: false,
                  mustHaveValueIfExist: false,
                },
              ],
            ],
          },
        ],
      },
    ],
  };
